// Logistics calendar mode hook — week anchor, fetch, day + route selection.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildWeekRollups,
  formatWeekRangeLabel,
  localWeekIsoBounds,
  weekDayKeys,
  type LogisticsCalendarDayRollup,
} from "@/lib/logistics/calendarLocalWeek";
import { clientLocalCalendarDay } from "@/lib/clientLocalDay";
import {
  getLogisticsRoutesCalendar,
  listLogisticsCarriers,
  listLogisticsUnits,
  type LogiCarrier,
  type LogiRoute,
} from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturing/types";

function todayLocalCalendarDay(): string {
  return clientLocalCalendarDay();
}

export function useLogisticsCalendarPanel(navTick: number) {
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [routes, setRoutes] = useState<LogiRoute[]>([]);
  const [units, setUnits] = useState<MfgUnit[]>([]);
  const [carriers, setCarriers] = useState<LogiCarrier[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(todayLocalCalendarDay());
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [detailNavTick, setDetailNavTick] = useState(0);

  const weekKeys = useMemo(() => weekDayKeys(weekAnchor), [weekAnchor]);
  const weekLabel = useMemo(() => formatWeekRangeLabel(weekKeys), [weekKeys]);
  const weekRollups = useMemo(
    () => buildWeekRollups(weekKeys, routes),
    [weekKeys, routes]
  );

  const selectedDayRollup: LogisticsCalendarDayRollup | null = useMemo(() => {
    if (!selectedDay) return null;
    return weekRollups.find((d) => d.date === selectedDay) ?? null;
  }, [weekRollups, selectedDay]);

  const reloadReferenceData = useCallback(async () => {
    const [unitsRes, carriersRes] = await Promise.all([
      listLogisticsUnits(),
      listLogisticsCarriers({ is_active: true }),
    ]);
    setUnits(unitsRes.ok && Array.isArray(unitsRes.data.units) ? unitsRes.data.units : []);
    setCarriers(
      carriersRes.ok && Array.isArray(carriersRes.data.carriers) ? carriersRes.data.carriers : []
    );
  }, []);

  const reloadWeek = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const bounds = localWeekIsoBounds(weekAnchor);
    const res = await getLogisticsRoutesCalendar({
      scheduled_from: bounds.from,
      scheduled_to: bounds.to,
    });
    setListBusy(false);
    if (!res.ok) {
      setRoutes([]);
      setLocalError(res.error);
      return;
    }
    setRoutes(Array.isArray(res.data.routes) ? res.data.routes : []);
  }, [weekAnchor]);

  useEffect(() => {
    void reloadReferenceData();
  }, [reloadReferenceData, navTick]);

  useEffect(() => {
    void reloadWeek();
  }, [reloadWeek, navTick]);

  const shiftWeek = useCallback((deltaWeeks: number) => {
    setWeekAnchor((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
    setSelectedRouteId(null);
  }, []);

  const goToToday = useCallback(() => {
    const today = new Date();
    setWeekAnchor(today);
    setSelectedDay(clientLocalCalendarDay());
    setSelectedRouteId(null);
  }, []);

  const selectDay = useCallback((date: string) => {
    setSelectedDay((prev) => (prev === date ? null : date));
    setSelectedRouteId(null);
  }, []);

  const selectRoute = useCallback((routeId: string) => {
    setSelectedRouteId((prev) => (prev === routeId ? null : routeId));
  }, []);

  const refreshAll = useCallback(() => {
    void reloadReferenceData();
    void reloadWeek();
    setDetailNavTick((s) => s + 1);
  }, [reloadReferenceData, reloadWeek]);

  const handleAfterStateChange = useCallback(() => {
    void reloadWeek();
    setDetailNavTick((s) => s + 1);
  }, [reloadWeek]);

  return {
    weekKeys,
    weekLabel,
    weekRollups,
    weekAnchor,
    listBusy,
    localError,
    units,
    carriers,
    selectedDay,
    selectedDayRollup,
    selectedRouteId,
    detailNavTick,
    shiftWeek,
    goToToday,
    selectDay,
    selectRoute,
    refreshAll,
    handleAfterStateChange,
  };
}
